import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveProfile, SaveProfilePayload } from '../../utils/api/natureDrApi';
import { useAuth } from '../../App';
import './Onboarding.css';

const TOTAL_STEPS = 5;

const ETHNICITY_OPTIONS = [
  'White',
  'Black / African / Caribbean',
  'Asian',
  'Mixed / Multiple',
  'Hispanic / Latino',
  'Middle Eastern / North African',
  'Other',
];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — DOB & Sex
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [biologicalSex, setBiologicalSex] = useState<'male' | 'female' | 'prefer_not_to_say' | ''>('');

  // Step 2 — Height & Weight
  const [heightUnit, setHeightUnit] = useState<'cm' | 'feet'>('cm');
  const [heightCm, setHeightCm] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weightUnit, setWeightUnit] = useState<'kg' | 'stone' | 'lbs'>('kg');
  const [weightKg, setWeightKg] = useState('');
  const [weightStone, setWeightStone] = useState('');
  const [weightLbs, setWeightLbs] = useState('');

  // Step 3 — Ethnicity & pregnancy
  const [ethnicity, setEthnicity] = useState('');
  const [isPregnant, setIsPregnant] = useState<boolean | undefined>(undefined);

  // Step 4 — Conditions
  const [conditions, setConditions] = useState<string[]>([]);
  const [conditionInput, setConditionInput] = useState('');

  // Step 5 — Medications
  const [medications, setMedications] = useState<string[]>([]);
  const [medicationInput, setMedicationInput] = useState('');

  const canProceed = (): boolean => {
    switch (step) {
      case 1:
        return !!dateOfBirth && !!biologicalSex;
      case 2: {
        const hasHeight = heightUnit === 'cm' ? Number(heightCm) > 0 : Number(heightFeet) > 0;
        const hasWeight = weightUnit === 'kg'
          ? Number(weightKg) > 0
          : weightUnit === 'stone'
            ? Number(weightStone) > 0
            : Number(weightLbs) > 0;
        return hasHeight && hasWeight;
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    setError(null);
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const handleBack = () => {
    setError(null);
    if (step > 1) setStep(step - 1);
  };

  const addTag = (list: string[], setList: (v: string[]) => void, input: string, setInput: (v: string) => void) => {
    const trimmed = input.trim();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setInput('');
  };

  const removeTag = (list: string[], setList: (v: string[]) => void, index: number) => {
    setList(list.filter((_, i) => i !== index));
  };

  const handleFinish = async () => {
    setSaving(true);
    setError(null);

    const payload: SaveProfilePayload = {
      dateOfBirth,
      biologicalSex: biologicalSex as 'male' | 'female' | 'prefer_not_to_say',
      heightUnit,
      weightUnit,
      ...(heightUnit === 'cm'
        ? { heightCm: Number(heightCm) }
        : { heightFeet: Number(heightFeet), heightInches: Number(heightInches) }),
      ...(weightUnit === 'kg'
        ? { weightKg: Number(weightKg) }
        : weightUnit === 'stone'
          ? { weightStone: Number(weightStone), weightLbs: Number(weightLbs) }
          : { weightLbs: Number(weightLbs) }),
      ...(ethnicity ? { ethnicity } : {}),
      ...(biologicalSex === 'female' && isPregnant !== undefined ? { isPregnant } : {}),
      ...(conditions.length > 0 ? { conditions } : {}),
      ...(medications.length > 0 ? { medications } : {}),
    };

    try {
      await saveProfile(payload);
      await refreshProfile();
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h1>Let's personalise your experience</h1>
          <p>
            We use this information to tailor blood analysis reference ranges and
            remedy recommendations specifically to you.
          </p>
        </div>

        <div className="onboarding-progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`progress-dot ${i + 1 === step ? 'active' : ''} ${i + 1 < step ? 'completed' : ''}`}
            />
          ))}
        </div>

        {/* Step 1: DOB & Sex */}
        {step === 1 && (
          <div className="onboarding-step">
            <div className="step-title">Basic Information</div>

            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                className="form-input"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label>Biological Sex</label>
              <div className="option-group">
                {(['male', 'female', 'prefer_not_to_say'] as const).map((opt) => (
                  <button
                    key={opt}
                    className={`option-btn ${biologicalSex === opt ? 'selected' : ''}`}
                    onClick={() => setBiologicalSex(opt)}
                    type="button"
                  >
                    {opt === 'prefer_not_to_say' ? 'Prefer not to say' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Height & Weight */}
        {step === 2 && (
          <div className="onboarding-step">
            <div className="step-title">Height & Weight</div>

            <div className="form-group">
              <label>Height</label>
              <div className="measurement-row">
                {heightUnit === 'cm' ? (
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Height in cm"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    min="0"
                  />
                ) : (
                  <div className="imperial-fields">
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Feet"
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      min="0"
                    />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Inches"
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      min="0"
                      max="11"
                    />
                  </div>
                )}
                <select
                  className="form-select"
                  value={heightUnit}
                  onChange={(e) => setHeightUnit(e.target.value as 'cm' | 'feet')}
                >
                  <option value="cm">cm</option>
                  <option value="feet">ft / in</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Weight</label>
              <div className="measurement-row">
                {weightUnit === 'kg' ? (
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Weight in kg"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    min="0"
                  />
                ) : weightUnit === 'stone' ? (
                  <div className="imperial-fields">
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Stone"
                      value={weightStone}
                      onChange={(e) => setWeightStone(e.target.value)}
                      min="0"
                    />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Lbs"
                      value={weightLbs}
                      onChange={(e) => setWeightLbs(e.target.value)}
                      min="0"
                    />
                  </div>
                ) : (
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Weight in lbs"
                    value={weightLbs}
                    onChange={(e) => setWeightLbs(e.target.value)}
                    min="0"
                  />
                )}
                <select
                  className="form-select"
                  value={weightUnit}
                  onChange={(e) => setWeightUnit(e.target.value as 'kg' | 'stone' | 'lbs')}
                >
                  <option value="kg">kg</option>
                  <option value="stone">st / lbs</option>
                  <option value="lbs">lbs</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Ethnicity & Pregnancy */}
        {step === 3 && (
          <div className="onboarding-step">
            <div className="step-title">Additional Details</div>

            <div className="form-group">
              <label>Ethnicity <span className="hint">(optional but helps personalise ranges)</span></label>
              <select
                className="form-select"
                value={ethnicity}
                onChange={(e) => setEthnicity(e.target.value)}
              >
                <option value="">Select...</option>
                {ETHNICITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {biologicalSex === 'female' && (
              <div className="form-group">
                <label>Are you currently pregnant?</label>
                <div className="option-group">
                  <button
                    className={`option-btn ${isPregnant === true ? 'selected' : ''}`}
                    onClick={() => setIsPregnant(true)}
                    type="button"
                  >
                    Yes
                  </button>
                  <button
                    className={`option-btn ${isPregnant === false ? 'selected' : ''}`}
                    onClick={() => setIsPregnant(false)}
                    type="button"
                  >
                    No
                  </button>
                </div>
                <p className="pregnancy-notice">
                  Pregnancy significantly affects blood marker reference ranges.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Conditions */}
        {step === 4 && (
          <div className="onboarding-step">
            <div className="step-title">Known Conditions <span className="hint">(optional)</span></div>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: 'var(--spacing-md)' }}>
              Add any diagnosed medical conditions (e.g. diabetes, hypothyroidism, PCOS).
            </p>
            <div className="form-group">
              <div className="tags-input-wrapper">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Type a condition..."
                  value={conditionInput}
                  onChange={(e) => setConditionInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, () => addTag(conditions, setConditions, conditionInput, setConditionInput))}
                />
                <button
                  className="add-tag-btn"
                  type="button"
                  onClick={() => addTag(conditions, setConditions, conditionInput, setConditionInput)}
                >
                  Add
                </button>
              </div>
              {conditions.length > 0 && (
                <div className="tags-list">
                  {conditions.map((c, i) => (
                    <span key={i} className="tag">
                      {c}
                      <button type="button" onClick={() => removeTag(conditions, setConditions, i)}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 5: Medications */}
        {step === 5 && (
          <div className="onboarding-step">
            <div className="step-title">Current Medications <span className="hint">(optional)</span></div>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: 'var(--spacing-md)' }}>
              Add any medications you currently take. This helps us flag potential interactions with natural remedies.
            </p>
            <div className="form-group">
              <div className="tags-input-wrapper">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Type a medication..."
                  value={medicationInput}
                  onChange={(e) => setMedicationInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, () => addTag(medications, setMedications, medicationInput, setMedicationInput))}
                />
                <button
                  className="add-tag-btn"
                  type="button"
                  onClick={() => addTag(medications, setMedications, medicationInput, setMedicationInput)}
                >
                  Add
                </button>
              </div>
              {medications.length > 0 && (
                <div className="tags-list">
                  {medications.map((m, i) => (
                    <span key={i} className="tag">
                      {m}
                      <button type="button" onClick={() => removeTag(medications, setMedications, i)}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {error && <div className="onboarding-error">{error}</div>}

        <div className="onboarding-nav">
          {step > 1 && (
            <button className="nav-btn back" onClick={handleBack} type="button">
              Back
            </button>
          )}
          {step < TOTAL_STEPS ? (
            <button
              className="nav-btn next"
              onClick={handleNext}
              disabled={!canProceed()}
              type="button"
            >
              Next
            </button>
          ) : (
            <button
              className="nav-btn finish"
              onClick={handleFinish}
              disabled={saving}
              type="button"
            >
              {saving ? 'Saving...' : 'Finish'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
